import React, { useContext } from 'react';
import { Col, Row } from 'react-bootstrap';
import { isMobile, isTablet } from 'react-device-detect';
import Carousel from 'react-multi-carousel';
import { IProduct } from '../../interfaces';
import {
  getProductDisplayPrice,
  getProductDisplayName
} from '../../utils/product';
import { AppContext } from '../AppContext';
import { CustomSliderArrow } from './Slider';
import { useTranslation } from '../../i18n';
import {
  getNudgeCurrencyCountryCode,
  jumpToAnchor,
  scrollElementIntoViewById
} from '../../utils/window';
import FocusButton from '../Common/FocusButton';
import smoothscroll from 'smoothscroll-polyfill';
import { currentLanguage } from '../../utils/language';
import Link from '../Common/Link';
import { useRouter } from 'next/router';

const responsive = {
  desktop: {
    breakpoint: { max: 4000, min: 1024 },
    items: 5
  },
  tablet: {
    breakpoint: { max: 1024, min: 800 },
    items: 4
  },
  largemobile: {
    breakpoint: { max: 800, min: 400 },
    items: 3,
    partialVisibilityGutter: 30
  },
  mobile: {
    breakpoint: { max: 400, min: 0 },
    items: 2,
    partialVisibilityGutter: 40
  }
};

interface Props {
  products: IProduct[];
  sectionTitle?: string;
  onFocusItem?: (itemIndex: string) => void;
  focusedItem?: string;
}

export const ProductThumbnail = ({
  product,
  id = ''
}: {
  product: IProduct;
  id?: string;
}) => {
  const { country, store, storeConfig } = useContext(AppContext);
  const router = useRouter();
  const { nudgeId} = router.query || {};

  const { i18n } = useTranslation();
  const language = currentLanguage(i18n);
  const displayCurrencyCountryCode =
    getNudgeCurrencyCountryCode() || store?.countryCode || country;
  const price = getProductDisplayPrice(
    product,
    displayCurrencyCountryCode,
    storeConfig?.strictProductDisplay
  );
  return (
    <Link
      href={`/products/[id]${nudgeId? `?nudgeId=${nudgeId}`: ''}`}
      as={`/products/${product.id}${nudgeId? `?nudgeId=${nudgeId}`: ''}`}
      className="thumbnail"
      id={id}
    >
      <div
        className="img-container"
        style={{ backgroundImage: `url(${product.previewImageUrl})` }}
      >
        <img src="/asset/square_ratio_maintainer.png" alt="" />
      </div>
      <p className="prod-title">{getProductDisplayName(product, language)}</p>
      <p className="price">{price}</p>
      <style jsx>
        {`
          .prod-title {
            font-size: 0.8em;
            text-transform: uppercase;
            font-weight: 600;
            width: 100%;
            margin-top: 10px;
            margin-bottom: 0;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          @media (min-width: 768px) {
            .prod-title {
              font-weight: 500;
              min-height: 38px;
            }
          }
          .price {
            margin-top: 10px;
            font-size: 0.8em;
          }
        `}
      </style>
    </Link>
  );
};

const MobileList = ({ products, focusedItem, onFocusItem }: Props) => (
  <Row className="products-list mobile-list">
    <div className="horizontal-scroll">
      {products.map((product: IProduct, index: number) => (
        <div>
          {onFocusItem && (
            <FocusButton
              onFocus={onFocusItem}
              itemId={`${index}`}
              focusedItem={focusedItem}
              className="focus-btn-product with-shadow"
            />
          )}
          <ProductThumbnail
            key={index}
            id={`product-${index}`}
            product={product}
          />
        </div>
      ))}
      <style jsx>{`
        .horizontal-scroll > div {
          display: inline-block;
        }
      `}</style>
    </div>
  </Row>
);

const DesktopList = ({ products, focusedItem, onFocusItem }: Props) => {
  return (
    <Row className="products-list desktop-list">
      <Col xs={12}>
        {products && (
          <Carousel
            responsive={responsive}
            ssr={false}
            removeArrowOnDeviceType={['mobile']}
            showDots={true}
            renderButtonGroupOutside={true}
            renderDotsOutside={true}
            infinite={true}
            customRightArrow={<CustomSliderArrow type="next" />}
            customLeftArrow={<CustomSliderArrow type="prev" />}
          >
            {products.map((product: IProduct, index: number) => (
              <div key={product.id}>
                {onFocusItem && (
                  <FocusButton
                    onFocus={onFocusItem}
                    itemId={`${index}`}
                    focusedItem={focusedItem}
                    className="focus-btn-product with-shadow"
                  />
                )}
                <ProductThumbnail
                  key={index}
                  id={`product-${index}`}
                  product={product}
                />
              </div>
            ))}
          </Carousel>
        )}
      </Col>
      <style jsx global>{`
        .react-multi-carousel-track {
          margin: auto;
        }
      `}</style>
    </Row>
  );
};

const ProductList = ({
  products,
  onFocusItem,
  focusedItem
}: {
  products: IProduct[] | null;
  onFocusItem?: (itemIndex: string) => void;
  focusedItem?: string;
}) => {
  const [mobile, setMobile] = React.useState(false);

  React.useEffect(() => {
    if (focusedItem) {
      if (!mobile) {
        setTimeout(() => {
          const elem: HTMLElement = document.querySelector(
            ".similar-products .react-multi-carousel-dot-list [data-index='" +
              focusedItem +
              "'] button"
          ) as HTMLElement;
          if (elem) elem.click();
        }, 20);
      }

      if (!onFocusItem) {
        jumpToAnchor('#product-slider-anchor');
        if (mobile) {
          smoothscroll.polyfill();
          setTimeout(() => {
            scrollElementIntoViewById(`product-${focusedItem}`);
          }, 300);
        }
      }
    }
  }, [focusedItem, mobile]);

  React.useEffect(() => {
    setMobile(isMobile && !isTablet);
  }, []);
  return mobile ? (
    <MobileList
      products={products || []}
      onFocusItem={onFocusItem}
      focusedItem={focusedItem}
    />
  ) : (
    <DesktopList
      products={products || []}
      onFocusItem={onFocusItem}
      focusedItem={focusedItem}
    />
  );
};

export default ProductList;
